import type { ReactElement } from "react";

import { type ToasterProps, toast as sonnerToast } from "sonner";

import { coerceToastMessage, coerceToastText } from "./apiErrorMessage";

/**
 * El contenido de un toast se declara `unknown` a proposito.
 *
 * Antes estaba tipado `string`, y ese tipo MENTIA: los errores de axios y de
 * react-query llegan como `any`, asi que en runtime entraba el diccionario de
 * errores de DRF (`{ prospectus_email: ["This field may not be blank."] }`).
 * Al renderizarlo, React 19 lanzaba "Objects are not valid as a React child" y
 * tumbaba la app entera, porque el `<Toaster/>` esta montado fuera del
 * ErrorBoundary (ver src/main.tsx vs src/App.tsx).
 *
 * Con `unknown` el tipo deja de mentir y `coerceToastMessage` garantiza que lo
 * que llega a sonner es texto mostrable. `options.action` NO se toca: ese campo
 * si lleva nodos de React legitimos.
 */
type ToastMessage = unknown;
type ToastPosition = NonNullable<ToasterProps["position"]>;
type ToastResolver<T> = ToastMessage | ((arg: T) => ToastMessage);

/**
 * `coerce` se pasa por parametro porque NO todos los toasts se tratan igual:
 * el de error puede degradar al texto generico (su contenido viene del
 * backend), los demas no — su texto lo escribe la app y degradarlo convertia un
 * exito en "Algo salio mal". Ver `coerceToastText` en apiErrorMessage.ts.
 */
const resolve = <T>(
  value: ToastResolver<T>,
  coerce: (v: unknown) => unknown,
) => {
  if (typeof value === "function") {
    return (arg: T) =>
      coerce((value as (arg: T) => ToastMessage)(arg)) as string;
  }
  return coerce(value) as string;
};

export const toast = {
  success: (
    message: ToastMessage,
    options?: { duration?: number; action?: any },
  ) => {
    return sonnerToast.success(coerceToastText(message) as string, {
      duration: options?.duration ?? 4000,
      action: options?.action,
    });
  },

  error: (
    message: ToastMessage,
    options?: { duration?: number; action?: any },
  ) => {
    return sonnerToast.error(coerceToastMessage(message) as string, {
      duration: options?.duration ?? 5000,
      action: options?.action,
    });
  },

  info: (message: ToastMessage, options?: { duration?: number }) => {
    return sonnerToast.info(coerceToastText(message) as string, {
      duration: options?.duration ?? 4000,
    });
  },

  warning: (message: ToastMessage, options?: { duration?: number }) => {
    return sonnerToast.warning(coerceToastText(message) as string, {
      duration: options?.duration ?? 5000,
    });
  },

  loading: (message: ToastMessage) => {
    return sonnerToast.loading(coerceToastText(message) as string);
  },

  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: ToastMessage;
      success: ToastResolver<T>;
      error: ToastResolver<any>;
    },
  ) => {
    return sonnerToast.promise(promise, {
      loading: coerceToastText(messages.loading) as string,
      success: resolve<T>(messages.success, coerceToastText),
      error: resolve<any>(messages.error, coerceToastMessage),
    });
  },

  /**
   * Un toast con contenido propio (JSX), sin los estilos de sonner. Lo que pinte `render` lo
   * escribe la app, así que no pasa por la coerción de texto.
   */
  custom: (
    render: (id: string | number) => ReactElement,
    options?: {
      id?: string | number;
      duration?: number;
      position?: ToastPosition;
    },
  ) => {
    return sonnerToast.custom(render, options);
  },

  dismiss: (toastId?: string | number) => {
    return sonnerToast.dismiss(toastId);
  },
};
